// src/components/familyoffice/SalePreparation.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useTranslation } from "react-i18next";
import {
  FileText,
  AlertCircle,
  CheckCircle,
  Download,
  Mail,
  X,
} from "lucide-react";

// 🔧 Paramétrage des exigences par pays de vente
const COUNTRY_RULESETS = {
  FR: {
    label: "France",
    legallyRequired: [
      "title",
      "artist",
      "date",
      "medium",
      "dimensions",
      "provenance",
      "condition",
      "valuation",
    ],
    ownerAttestations: {
      authenticity: true, // coche obligatoire si pas de certificat
      property: true, // coche obligatoire
    },
    recommended: [
      "images",
      "condition_report",
      "purchase_location",
      "certification",
    ],
  },
  DEFAULT: {
    label: "Standard international",
    legallyRequired: [
      "title",
      "artist",
      "date",
      "medium",
      "dimensions",
      "provenance",
      "condition",
    ],
    ownerAttestations: {
      authenticity: true,
      property: true,
    },
    recommended: ["images", "valuation"],
  },
};

const COPY = {
  fr: {
    analysing: "Analyse de l'œuvre en cours…",
    heading: "Dossier de mise en vente",
    complete: "Complet",
    mandatory: "Obligatoire pour la vente dans ce pays",
    recommended: "Information recommandée",
    responsible: "PERSONNE RESPONSABLE DE LA MISE EN VENTE",
    expert: "Expert",
    lawyer: "Avocat",
    pickExpert: "Sélectionner l’expert qui prendra en charge la vente",
    noExpert: "— Aucun expert sélectionné —",
    contact: "Contacter",
    phName: "Nom",
    phFirm: "Cabinet",
    phEmail: "Email",
    phPhone: "Téléphone",
    lawyerNeed: "Nom et email sont obligatoires pour générer le dossier si vous choisissez un avocat.",
    viewNda: "Voir / éditer le NDA",
    close: "Fermer",
    download: "Télécharger le dossier (.zip)",
    ndaTitle: "Accord de confidentialité (NDA)",
    ndaSave: "Sauvegarder dans le ZIP",
    untitled: "Sans titre",
    unknownArtist: "Artiste inconnu",
    noNameExpert: "Expert sans nom",
    notProvided: "Non renseigné",
    okDownloaded: "Dossier téléchargé avec succès !",
    errGen: "Erreur lors de la génération du dossier. Vérifiez que toutes les librairies sont installées.",
    ndaUpdated: "NDA mis à jour pour ce dossier (la version texte sera incluse dans le ZIP).",
    saleCountry: "Pays de vente :",
    rules: "Règles :",
    emailLbl: "Email :",
    ownerFO: "Le propriétaire / Family Office",
    authBold: "garantit l’authenticité",
    authRest: "de l’œuvre, en l’absence de certificat d’authenticité, conformément à son obligation de délivrance (art. 1603 du Code civil).",
    propBold: "atteste être le détenteur légitime",
    propRest: "de l’œuvre, qu’elle n’est pas issue d’un vol ou d’un trafic illicite, conformément à l’article 2279 du Code civil et, le cas échéant, à la Convention UNESCO de 1970.",
    blockFields: "que tous les champs obligatoires pour ce pays soient complétés",
    blockContact: "désigner un expert ou un avocat (nom + email)",
    blockAuth: "cocher la garantie d’authenticité",
    blockProp: "cocher l’attestation de propriété",
    msgFill: "Compléter les champs obligatoires :",
    msgContact: "Désigner un EXPERT ou un AVOCAT (nom + email).",
    msgAuth: "Cocher la garantie d’authenticité.",
    msgProp: "Cocher l’attestation de propriété légitime.",
    fields: {images: "Visuels HD", title: "Titre", artist: "Artiste", date: "Date/Année", medium: "Medium", signature: "Signature", certification: "Certificat d'authenticité", dimensions: "Dimensions", condition: "État de conservation", condition_report: "Condition report (document)", provenance: "Provenance", purchase_location: "Lieu d'achat (ville/pays)", valuation: "Estimation de valeur"},
    countries: {FR: "France", DEFAULT: "Standard international"},
    missing: (n) => `${n} manquante${n > 1 ? "s" : ""}`,
    nVisuals: (n) => `${n} visuel${n > 1 ? "s" : ""}`,
    blockHead: (country) => `Pour générer le dossier pour la vente en ${country}, il faut :`,
    msgHead: (country) => `Le dossier ne peut pas être généré.\n\nPour une vente en ${country}, il faut :\n`,
  },
  en: {
    analysing: "Analysing the artwork…",
    heading: "Sale file",
    complete: "Complete",
    mandatory: "Mandatory for a sale in this country",
    recommended: "Recommended information",
    responsible: "PERSON RESPONSIBLE FOR THE SALE",
    expert: "Expert",
    lawyer: "Lawyer",
    pickExpert: "Select the expert who will handle the sale",
    noExpert: "— No expert selected —",
    contact: "Contact",
    phName: "Name",
    phFirm: "Firm",
    phEmail: "Email",
    phPhone: "Phone",
    lawyerNeed: "Name and email are required to generate the file if you choose a lawyer.",
    viewNda: "View / edit the NDA",
    close: "Close",
    download: "Download the file (.zip)",
    ndaTitle: "Confidentiality agreement (NDA)",
    ndaSave: "Save in the ZIP",
    untitled: "Untitled",
    unknownArtist: "Unknown artist",
    noNameExpert: "Expert without a name",
    notProvided: "Not provided",
    okDownloaded: "File downloaded successfully!",
    errGen: "Error while generating the file. Check that all the libraries are installed.",
    ndaUpdated: "NDA updated for this file (the text version will be included in the ZIP).",
    saleCountry: "Country of sale:",
    rules: "Rules:",
    emailLbl: "Email:",
    ownerFO: "The owner / Family Office",
    authBold: "guarantees the authenticity",
    authRest: "of the artwork, in the absence of a certificate of authenticity, in accordance with its delivery obligation (art. 1603 of the French Civil Code).",
    propBold: "certifies being the legitimate holder",
    propRest: "of the artwork, and that it does not originate from theft or illicit trafficking, in accordance with article 2279 of the French Civil Code and, where applicable, the 1970 UNESCO Convention.",
    blockFields: "that all the mandatory fields for this country are completed",
    blockContact: "designate an expert or a lawyer (name + email)",
    blockAuth: "tick the authenticity guarantee",
    blockProp: "tick the ownership attestation",
    msgFill: "Complete the mandatory fields:",
    msgContact: "Designate an EXPERT or a LAWYER (name + email).",
    msgAuth: "Tick the authenticity guarantee.",
    msgProp: "Tick the legitimate-ownership attestation.",
    fields: {images: "HD visuals", title: "Title", artist: "Artist", date: "Date/Year", medium: "Medium", signature: "Signature", certification: "Certificate of authenticity", dimensions: "Dimensions", condition: "Condition", condition_report: "Condition report (document)", provenance: "Provenance", purchase_location: "Place of purchase (city/country)", valuation: "Value estimate"},
    countries: {FR: "France", DEFAULT: "International standard"},
    missing: (n) => `${n} missing`,
    nVisuals: (n) => `${n} visual${n === 1 ? "" : "s"}`,
    blockHead: (country) => `To generate the file for a sale in ${country}, you need to:`,
    msgHead: (country) => `The file cannot be generated.\n\nFor a sale in ${country}, you need to:\n`,
  },
  de: {
    analysing: "Werk wird analysiert…",
    heading: "Verkaufsdossier",
    complete: "Vollständig",
    mandatory: "Für einen Verkauf in diesem Land erforderlich",
    recommended: "Empfohlene Angabe",
    responsible: "FÜR DEN VERKAUF VERANTWORTLICHE PERSON",
    expert: "Experte",
    lawyer: "Anwalt",
    pickExpert: "Wählen Sie den Experten, der den Verkauf betreut",
    noExpert: "— Kein Experte ausgewählt —",
    contact: "Kontaktieren",
    phName: "Name",
    phFirm: "Kanzlei",
    phEmail: "E-Mail",
    phPhone: "Telefon",
    lawyerNeed: "Name und E-Mail sind zum Erstellen des Dossiers erforderlich, wenn Sie einen Anwalt wählen.",
    viewNda: "NDA ansehen / bearbeiten",
    close: "Schließen",
    download: "Dossier herunterladen (.zip)",
    ndaTitle: "Vertraulichkeitsvereinbarung (NDA)",
    ndaSave: "Im ZIP speichern",
    untitled: "Ohne Titel",
    unknownArtist: "Unbekannter Künstler",
    noNameExpert: "Experte ohne Namen",
    notProvided: "Nicht angegeben",
    okDownloaded: "Dossier erfolgreich heruntergeladen!",
    errGen: "Fehler beim Erstellen des Dossiers. Prüfen Sie, ob alle Bibliotheken installiert sind.",
    ndaUpdated: "NDA für dieses Dossier aktualisiert (die Textversion wird im ZIP enthalten sein).",
    saleCountry: "Verkaufsland:",
    rules: "Regeln:",
    emailLbl: "E-Mail:",
    ownerFO: "Der Eigentümer / das Family Office",
    authBold: "garantiert die Echtheit",
    authRest: "des Werks, bei fehlendem Echtheitszertifikat, gemäß seiner Lieferpflicht (Art. 1603 des französischen Code civil).",
    propBold: "bestätigt, der rechtmäßige Inhaber zu sein",
    propRest: "des Werks und dass es nicht aus Diebstahl oder illegalem Handel stammt, gemäß Artikel 2279 des französischen Code civil und gegebenenfalls dem UNESCO-Übereinkommen von 1970.",
    blockFields: "dass alle in diesem Land erforderlichen Felder ausgefüllt sind",
    blockContact: "einen Experten oder Anwalt benennen (Name + E-Mail)",
    blockAuth: "die Echtheitsgarantie ankreuzen",
    blockProp: "die Eigentumsbestätigung ankreuzen",
    msgFill: "Pflichtfelder ausfüllen:",
    msgContact: "Einen EXPERTEN oder ANWALT benennen (Name + E-Mail).",
    msgAuth: "Die Echtheitsgarantie ankreuzen.",
    msgProp: "Die Bestätigung des rechtmäßigen Eigentums ankreuzen.",
    fields: {images: "HD-Bilder", title: "Titel", artist: "Künstler", date: "Datum/Jahr", medium: "Medium", signature: "Signatur", certification: "Echtheitszertifikat", dimensions: "Abmessungen", condition: "Erhaltungszustand", condition_report: "Zustandsbericht (Dokument)", provenance: "Provenienz", purchase_location: "Kaufort (Stadt/Land)", valuation: "Wertschätzung"},
    countries: {FR: "Frankreich", DEFAULT: "Internationaler Standard"},
    missing: (n) => `${n} fehlend`,
    nVisuals: (n) => `${n} ${n === 1 ? "Bild" : "Bilder"}`,
    blockHead: (country) => `Um das Dossier für einen Verkauf in ${country} zu erstellen, müssen Sie:`,
    msgHead: (country) => `Das Dossier kann nicht erstellt werden.\n\nFür einen Verkauf in ${country} müssen Sie:\n`,
  },
};

function useUi() {
  const { i18n } = useTranslation();
  const lg = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  return COPY[lg] || COPY.fr;
}

export default function SalePreparation({ artwork, onClose }) {
  const ui = useUi();
  const [loading, setLoading] = useState(true);
  const [completeness, setCompleteness] = useState({});
  const [showNDA, setShowNDA] = useState(false);
  const [ndaText, setNdaText] = useState("");
  const [experts, setExperts] = useState([]);

  // Pays de vente (FR par défaut pour l’instant)
  const [saleCountry, setSaleCountry] = useState("FR");

  // Contact = expert ou avocat
  const [contactMode, setContactMode] = useState("expert"); // "expert" | "lawyer"
  const [selectedExpertId, setSelectedExpertId] = useState("");

  const [lawyerInfo, setLawyerInfo] = useState({
    name: "",
    firm: "",
    email: "",
    phone: "",
  });

  // Attestations FO / propriétaire
  const [userCheckedAuthenticity, setUserCheckedAuthenticity] =
    useState(false);
  const [userCheckedProperty, setUserCheckedProperty] = useState(false);

  // Charger complétude + experts au montage / changement d’œuvre
  useEffect(() => {
    setLoading(true);
    setUserCheckedAuthenticity(false);
    setUserCheckedProperty(false);
    checkCompleteness();
    loadExperts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork]);

  // Régénérer le NDA quand les infos clés changent
  useEffect(() => {
    if (!loading) {
      generateDefaultNDA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contactMode,
    lawyerInfo,
    selectedExpertId,
    loading,
    userCheckedAuthenticity,
    userCheckedProperty,
    saleCountry,
  ]);

  // -------- Vérifier la complétude des données --------
  const checkCompleteness = () => {
    // Propreté provenance
    let provenanceDisplay = null;
    if (artwork.provenance) {
      let provenanceData = artwork.provenance;

      if (typeof provenanceData === "string") {
        try {
          const trimmed = provenanceData.trim();
          if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            provenanceData = JSON.parse(trimmed);
          } else {
            provenanceDisplay = provenanceData;
          }
        } catch (e) {
          provenanceDisplay = provenanceData;
        }
      }

      if (!provenanceDisplay && provenanceData) {
        if (Array.isArray(provenanceData)) {
          provenanceDisplay = provenanceData
            .map((p) => {
              const date = p.date || "";
              const desc = p.description || p.location || "";
              return `${date} - ${desc}`
                .trim()
                .replace(/^- /, "")
                .replace(/ -$/, "");
            })
            .filter(Boolean)
            .join(" • ");
        } else if (typeof provenanceData === "object") {
          const date = provenanceData.date || "";
          const desc =
            provenanceData.description || provenanceData.location || "";
          provenanceDisplay = `${date} - ${desc}`
            .trim()
            .replace(/^- /, "")
            .replace(/ -$/, "");
        }
      }
    }

    const required = {
      images: {
        label: "Visuels HD",
        status: artwork.images?.length > 0 || artwork.image_url,
        data:
          artwork.images ||
          (artwork.image_url ? [artwork.image_url] : []),
      },
      title: {
        label: "Titre",
        status: !!artwork.title,
        data: artwork.title,
      },
      artist: {
        label: "Artiste",
        status: !!artwork.artist,
        data: artwork.artist,
      },
      date: {
        label: "Date/Année",
        status: !!artwork.year,
        data: artwork.year,
      },
      medium: {
        label: "Medium",
        status: !!artwork.medium,
        data: artwork.medium,
      },
      signature: {
        label: "Signature",
        status: !!artwork.signature,
        data: artwork.signature,
      },
      certification: {
        label: "Certificat d'authenticité",
        status: !!artwork.certificate_authenticity,
        data: artwork.certificate_authenticity,
      },
      dimensions: {
        label: "Dimensions",
        status: !!(
          artwork.specifications?.dimensions?.height ||
          artwork.specifications?.dimensions?.width
        ),
        data:
          artwork.specifications?.dimensions?.height &&
          artwork.specifications?.dimensions?.width
            ? `${artwork.specifications.dimensions.height} x ${
                artwork.specifications.dimensions.width
              }${
                artwork.specifications.dimensions.depth
                  ? ` x ${artwork.specifications.dimensions.depth}`
                  : ""
              } ${artwork.specifications.dimensions.unit || "cm"}`
            : null,
      },

      condition: {
        label: "État de conservation",
        status: !!artwork.condition,
        data: artwork.condition,
      },
      condition_report: {
        label: "Condition report (document)",
        status: !!(
          artwork.condition_report ||
          artwork.condition_report_url ||
          (Array.isArray(artwork.documents) &&
            artwork.documents.some(
              (d) => d && d.type === "condition_report"
            ))
        ),
        data:
          artwork.condition_report ||
          artwork.condition_report_url ||
          "Document disponible",
      },
      provenance: {
        label: "Provenance",
        status: !!provenanceDisplay,
        data: provenanceDisplay,
      },
      purchase_location: {
        label: "Lieu d'achat (ville/pays)",
        status: !!(artwork.purchase_city || artwork.purchase_country),
        data: [artwork.purchase_city, artwork.purchase_country]
          .filter(Boolean)
          .join(", "),
      },
      valuation: {
        label: "Estimation de valeur",
        status: !!(
          artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value
        ),
        data: artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value,
      },
    };

    setCompleteness(required);
    setLoading(false);
  };

  // -------- Charger les experts disponibles --------
  const loadExperts = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, company, email")
      .eq("role", "expert")
      .eq("approved", true)
      .limit(50);

    setExperts(data || []);
  };

  const selectedExpert =
    experts.find((e) => e.id === selectedExpertId) || null;

  // -------- Générer le NDA par défaut (texte éditable) --------
  const generateDefaultNDA = () => {
    const today = new Date().toLocaleDateString("fr-FR");
    const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;

    let representative = "Représenté par [NOM DU FAMILY OFFICE]";
    let contactInfo = "";

    if (contactMode === "lawyer" && lawyerInfo.name) {
      representative = `Représenté par Maître ${lawyerInfo.name}${
        lawyerInfo.firm ? ` - ${lawyerInfo.firm}` : ""
      }`;
      if (lawyerInfo.email) {
        contactInfo = `\nContact : ${lawyerInfo.email}${
          lawyerInfo.phone ? ` - ${lawyerInfo.phone}` : ""
        }`;
      }
    } else if (contactMode === "expert" && selectedExpert) {
      const fullName = `${selectedExpert.first_name || ""} ${
        selectedExpert.last_name || ""
      }`.trim();
      representative = `Représenté par ${fullName || "l'Expert désigné"}${
        selectedExpert.company ? ` - ${selectedExpert.company}` : ""
      }`;
      if (selectedExpert.email) {
        contactInfo = `\nContact : ${selectedExpert.email}`;
      }
    }

    const authenticityClause = rules.ownerAttestations.authenticity
      ? `Le Vendeur garantit l’authenticité de l’œuvre. En l’absence de certificat d’authenticité, cette garantie est donnée sous sa seule responsabilité, conformément à son obligation de délivrance (article 1603 du Code civil).`
      : "";

    const propertyClause = rules.ownerAttestations.property
      ? `Le Vendeur atteste être le propriétaire légitime de l’œuvre et garantit qu’elle n’est pas issue d’un vol, d’un trafic illicite ou d’un transfert illégal, conformément à l’article 2279 du Code civil et, le cas échéant, à la Convention de l’UNESCO de 1970.`
      : "";

    const nda = `ACCORD DE CONFIDENTIALITÉ

Entre les soussignés :

D'une part,
Le Propriétaire de l'œuvre d'art décrite ci-après
${representative}${contactInfo}

Et d'autre part,
Le Destinataire des informations confidentielles

Il a été convenu ce qui suit :

ARTICLE 1 - OBJET
Le présent accord a pour objet de définir les conditions dans lesquelles le Propriétaire communique au Destinataire des informations confidentielles concernant l'œuvre suivante :

Œuvre : ${artwork.title || "[Titre]"}
Artiste : ${artwork.artist || "[Artiste]"}
Date : ${artwork.year || "[Date]"}

ARTICLE 2 - DÉFINITION DES INFORMATIONS CONFIDENTIELLES
Sont considérées comme confidentielles toutes les informations relatives à :
- La provenance de l'œuvre
- Son état de conservation détaillé
- Son estimation de valeur
- L'identité du propriétaire actuel
- Les documents d'authentification
- Les coordonnées et localisation précise

ARTICLE 3 - GARANTIES DU VENDEUR
${authenticityClause || "[Garantie d’authenticité à préciser]"}
${propertyClause || "[Attestation de propriété à préciser]"}

ARTICLE 4 - OBLIGATIONS DU DESTINATAIRE
Le Destinataire s'engage à :
- Traiter les informations avec la plus stricte confidentialité
- Ne les utiliser que dans le cadre d'une éventuelle transaction
- Ne pas les divulguer à des tiers sans autorisation écrite préalable
- Restituer ou détruire tous documents confidentiels sur demande

ARTICLE 5 - DURÉE
Le présent accord prend effet à compter de sa signature et demeure valable pour une durée de 2 ans.

ARTICLE 6 - LOI APPLICABLE
Le présent accord est régi par le droit de ${
      rules.label || "la juridiction compétente"
    }.

Fait à [VILLE], le ${today}

Le Propriétaire                           Le Destinataire
[Signature]                               [Signature]`;

    setNdaText(nda);
  };

  // -------- Helper prix pour le PDF --------
  const formatPriceForPDF = (value) => {
    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : Number(value);

    if (isNaN(numValue)) return String(value || "");

    const parts = numValue.toFixed(0).toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return parts.join(",") + " EUR";
  };

  // -------- Générer le ZIP (NDA + PDF + images) --------
  const generateSaleDossier = async () => {
    const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;

    const legalRequiredKeys = rules.legallyRequired || [];
    const isLegallyComplete =
      legalRequiredKeys.length === 0 ||
      legalRequiredKeys.every((key) => completeness[key]?.status);

    const isLawyerValid =
      contactMode === "lawyer" && lawyerInfo.name && lawyerInfo.email;
    const isExpertValid =
      contactMode === "expert" && !!selectedExpertId;
    const isContactOk = isLawyerValid || isExpertValid;

    const needsAuthAttestation =
      rules.ownerAttestations?.authenticity &&
      !completeness.certification?.status;
    const isAuthenticityOk =
      !needsAuthAttestation || userCheckedAuthenticity;

    const needsPropertyAttestation =
      rules.ownerAttestations?.property ?? false;
    const isPropertyOk =
      !needsPropertyAttestation || userCheckedProperty;

    const isSaleAuthorized =
      isLegallyComplete && isContactOk && isAuthenticityOk && isPropertyOk;

    if (!isSaleAuthorized) {
      const missingLegal = (rules.legallyRequired || []).filter(
        (key) => !completeness[key]?.status
      );
      let msg = ui.msgHead(uiCountry);
      if (missingLegal.length > 0) {
        msg += `\n• ${ui.msgFill} ${missingLegal
          .map((k) => fl(k))
          .filter(Boolean)
          .join(", ")}.`;
      }
      if (!isContactOk) {
        msg += `\n• ${ui.msgContact}`;
      }
      if (!isAuthenticityOk) {
        msg += `\n• ${ui.msgAuth}`;
      }
      if (!isPropertyOk) {
        msg += `\n• ${ui.msgProp}`;
      }
      alert(msg);
      return;
    }

    try {
      const JSZip = (await import("jszip")).default;
      const {
        Document,
        Paragraph,
        TextRun,
        AlignmentType,
        HeadingLevel,
        Packer,
      } = await import("docx");
      const { jsPDF } = await import("jspdf");

      const zip = new JSZip();

      // 1️⃣ NDA Word
      const ruleset = rules;

      const ndaDoc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "ACCORD DE CONFIDENTIALITÉ",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Entre les soussignés :",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({ text: "" }),
              new Paragraph({ text: "D'une part," }),
              new Paragraph({
                text: "Le Propriétaire de l'œuvre d'art décrite ci-après",
              }),
              new Paragraph({
                text:
                  contactMode === "lawyer" && lawyerInfo.name
                    ? `Représenté par Maître ${lawyerInfo.name}${
                        lawyerInfo.firm ? ` - ${lawyerInfo.firm}` : ""
                      }`
                    : contactMode === "expert" && selectedExpert
                    ? `Représenté par ${
                        `${selectedExpert.first_name || ""} ${
                          selectedExpert.last_name || ""
                        }`.trim() || "l'Expert désigné"
                      }${
                        selectedExpert.company
                          ? ` - ${selectedExpert.company}`
                          : ""
                      }`
                    : "Représenté par [NOM DU FAMILY OFFICE]",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({ text: "Et d'autre part," }),
              new Paragraph({
                text: "Le Destinataire des informations confidentielles",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 1 - OBJET",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: `Le présent accord a pour objet de définir les conditions dans lesquelles le Propriétaire communique au Destinataire des informations confidentielles concernant l'œuvre suivante :`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: `Œuvre : ${artwork.title || "[Titre]"}`,
              }),
              new Paragraph({
                text: `Artiste : ${artwork.artist || "[Artiste]"}`,
              }),
              new Paragraph({
                text: `Date : ${artwork.year || "[Date]"}`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 2 - INFORMATIONS CONFIDENTIELLES",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Sont considérées comme confidentielles toutes les informations relatives notamment à :",
              }),
              new Paragraph({ text: "- La provenance de l'œuvre" }),
              new Paragraph({
                text: "- Son état de conservation détaillé",
              }),
              new Paragraph({ text: "- Son estimation de valeur" }),
              new Paragraph({
                text: "- L'identité du propriétaire actuel",
              }),
              new Paragraph({
                text: "- Les documents d'authentification",
              }),
              new Paragraph({
                text: "- Les coordonnées et localisation précise",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 3 - GARANTIES DU VENDEUR",
                    bold: true,
                  }),
                ],
              }),
              ruleset.ownerAttestations?.authenticity
                ? new Paragraph({
                    text: "Le Vendeur garantit l’authenticité de l’œuvre. En l’absence de certificat d’authenticité, cette garantie est donnée sous sa seule responsabilité, conformément à son obligation de délivrance (article 1603 du Code civil).",
                  })
                : new Paragraph({
                    text: "[Garantie d’authenticité à préciser]",
                  }),
              ruleset.ownerAttestations?.property
                ? new Paragraph({
                    text: "Le Vendeur atteste être le propriétaire légitime de l’œuvre et garantit qu’elle n’est pas issue d’un vol, d’un trafic illicite ou d’un transfert illégal, conformément à l’article 2279 du Code civil et, le cas échéant, à la Convention de l’UNESCO de 1970.",
                  })
                : new Paragraph({
                    text: "[Attestation de propriété à préciser]",
                  }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 4 - OBLIGATIONS DU DESTINATAIRE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Le Destinataire s'engage à traiter les informations avec la plus stricte confidentialité, à ne les utiliser que dans le cadre d'une éventuelle transaction, à ne pas les divulguer à des tiers sans autorisation écrite préalable, et à restituer ou détruire tous documents confidentiels sur demande.",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 5 - DURÉE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Le présent accord prend effet à compter de sa signature et demeure valable pour une durée de 2 ans.",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 6 - LOI APPLICABLE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: `Le présent accord est régi par le droit de ${
                  ruleset.label || "la juridiction compétente"
                }.`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: `Fait à [VILLE], le ${new Date().toLocaleDateString(
                  "fr-FR"
                )}`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: "Le Propriétaire                           Le Destinataire",
              }),
              new Paragraph({
                text: "[Signature]                               [Signature]",
              }),
            ],
          },
        ],
      });

      const ndaBlob = await Packer.toBlob(ndaDoc);
      zip.file("NDA.docx", ndaBlob);

      // 2️⃣ PDF dossier
      const pdf = new jsPDF();
      let yPos = 20;

      pdf.setFontSize(20);
      pdf.text("Dossier de mise en vente", 20, yPos);
      yPos += 15;

      pdf.setFontSize(12);
      pdf.text(`${artwork.title || "Sans titre"}`, 20, yPos);
      yPos += 7;
      pdf.setFontSize(10);
      pdf.text(
        `${artwork.artist || "Artiste inconnu"} - ${
          artwork.year || "Date inconnue"
        }`,
        20,
        yPos
      );
      yPos += 15;

      pdf.setFontSize(11);
      Object.entries(completeness).forEach(([key, info]) => {
        if (info.status && key !== "images") {
          const label = info.label;
          let value = "";

          if (key === "valuation") {
            value = formatPriceForPDF(info.data);
          } else {
            value = String(info.data || "");
          }

          pdf.setFont(undefined, "bold");
          pdf.text(`${label}:`, 20, yPos);
          pdf.setFont(undefined, "normal");

          const lines = pdf.splitTextToSize(value, 170);
          pdf.text(lines, 20, yPos + 5);
          yPos += 5 + lines.length * 5 + 3;

          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
        }
      });

      // Images note
      yPos += 5;
      if (!artwork.images?.length && !artwork.image_url) {
        if (yPos > 260) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.setFont(undefined, "italic");
        pdf.setFontSize(9);
        pdf.text(
          "Visuels disponibles sur demande après signature du NDA",
          20,
          yPos
        );
        pdf.setFontSize(10);
        pdf.setFont(undefined, "normal");
        yPos += 8;
      }

      // Contact
      yPos += 10;
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.setFont(undefined, "bold");
      pdf.text("Contact pour la vente:", 20, yPos);
      yPos += 7;
      pdf.setFont(undefined, "normal");

      if (contactMode === "lawyer" && lawyerInfo.name) {
        pdf.text(`Maître ${lawyerInfo.name}`, 20, yPos);
        yPos += 5;
        if (lawyerInfo.firm) {
          pdf.text(lawyerInfo.firm, 20, yPos);
          yPos += 5;
        }
        if (lawyerInfo.email) {
          pdf.text(lawyerInfo.email, 20, yPos);
          yPos += 5;
        }
        if (lawyerInfo.phone) {
          pdf.text(lawyerInfo.phone, 20, yPos);
        }
      } else if (contactMode === "expert" && selectedExpert) {
        const fullName = `${selectedExpert.first_name || ""} ${
          selectedExpert.last_name || ""
        }`.trim();
        pdf.text(fullName || "Expert désigné", 20, yPos);
        yPos += 5;
        if (selectedExpert.company) {
          pdf.text(selectedExpert.company, 20, yPos);
          yPos += 5;
        }
        if (selectedExpert.email) {
          pdf.text(selectedExpert.email, 20, yPos);
        }
      }

      const pdfBlob = pdf.output("blob");
      zip.file("Dossier_Vente.pdf", pdfBlob);

      // 3️⃣ Images
      if (artwork.images && artwork.images.length > 0) {
        const imagesFolder = zip.folder("images");
        for (let i = 0; i < artwork.images.length; i++) {
          const imageUrl = artwork.images[i];
          try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const ext =
              imageUrl.split(".").pop().split("?")[0] || "jpg";
            imagesFolder.file(`image_${i + 1}.${ext}`, blob);
          } catch (e) {
            console.warn(
              `Impossible de télécharger l'image ${i + 1}`,
              e
            );
          }
        }
      } else if (artwork.image_url) {
        try {
          const response = await fetch(artwork.image_url);
          const blob = await response.blob();
          const ext =
            artwork.image_url.split(".").pop().split("?")[0] ||
            "jpg";
          zip.file(`images/image_1.${ext}`, blob);
        } catch (e) {
          console.warn("Impossible de télécharger l'image", e);
        }
      }

      // 4️⃣ Téléchargement ZIP
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dossier_Vente_${(artwork.artist || "")
        .replace(/\s/g, "_")
        .slice(0, 50)}_${(artwork.title || "")
        .replace(/\s/g, "_")
        .slice(0, 50)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(ui.okDownloaded);
    } catch (error) {
      console.error("Erreur lors de la génération:", error);
      alert(
        ui.errGen
      );
    }
  };

  // -------- Contacter un expert par mail --------
  const contactExpert = () => {
    if (!selectedExpert) return;
    const subject = `Complément d'information requis - ${artwork.title}`;
    const body = `Bonjour,

Je prépare un dossier de mise en vente pour l'œuvre suivante et j'ai besoin de votre expertise pour compléter certaines informations :

Œuvre : ${artwork.title || "Sans titre"}
Artiste : ${artwork.artist || "Inconnu"}

Informations manquantes :
${Object.entries(completeness)
  .filter(([_, info]) => !info.status)
  .map(([_, info]) => `- ${info.label}`)
  .join("\n")}

Pourriez-vous m'aider à compléter ces informations ?

Cordialement`;

    window.location.href = `mailto:${
      selectedExpert.email
    }?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      body
    )}`;
  };

  const missingCount = Object.values(completeness).filter(
    (c) => !c.status
  ).length;
  const totalCount = Object.keys(completeness).length;
  const isComplete = totalCount > 0 && missingCount === 0;

  const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;
  const uiCountry = ui.countries[saleCountry] || ui.countries.DEFAULT;
  const fl = (k) => ui.fields[k] || completeness[k]?.label;
  const legalRequiredKeys = rules.legallyRequired || [];
  const isLegallyComplete =
    legalRequiredKeys.length === 0 ||
    legalRequiredKeys.every((key) => completeness[key]?.status);

  const needsAuthAttestation =
    rules.ownerAttestations?.authenticity &&
    !completeness.certification?.status;
  const needsPropertyAttestation =
    rules.ownerAttestations?.property ?? false;

  const isLawyerValid =
    contactMode === "lawyer" && lawyerInfo.name && lawyerInfo.email;
  const isExpertValid =
    contactMode === "expert" && !!selectedExpertId;
  const isContactOk = isLawyerValid || isExpertValid;

  const isAuthenticityOk =
    !needsAuthAttestation || userCheckedAuthenticity;
  const isPropertyOk =
    !needsPropertyAttestation || userCheckedProperty;

  const isSaleAuthorized =
    isLegallyComplete && isContactOk && isAuthenticityOk && isPropertyOk;

  if (loading && !totalCount) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl px-6 py-4 text-sm">
          {ui.analysing}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="bg-white border-b border-[#E8E8E8] px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-serif text-[#1A1A1A]">
              {ui.heading}
            </h2>
            <p className="text-xs text-[#666]">
              {artwork.title || ui.untitled} •{" "}
              {artwork.artist || ui.unknownArtist}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#666] hover:text-[#1A1A1A]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenu */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          {/* Pays de vente */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#666]">
              {ui.saleCountry}{" "}
              <select
                value={saleCountry}
                onChange={(e) => setSaleCountry(e.target.value)}
                className="ml-1 border border-[#E8E8E8] rounded px-1 py-0.5 text-[11px] bg-white"
              >
                <option value="FR">{ui.countries.FR}</option>
                {/* plus tard : autres pays */}
              </select>
            </p>
            <p className="text-[11px] text-[#999]">
              {ui.rules} {uiCountry}
            </p>
          </div>

          {/* Statut complétude */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {isComplete ? (
                <CheckCircle
                  className="text-emerald-600"
                  size={16}
                />
              ) : (
                <AlertCircle
                  className="text-amber-600"
                  size={16}
                />
              )}
              <span className="text-sm font-medium text-[#1A1A1A]">
                {isComplete
                  ? ui.complete
                  : ui.missing(missingCount)}
              </span>
            </div>
            <span className="text-sm text-[#666]">
              {totalCount - missingCount}/{totalCount}
            </span>
          </div>
          <div className="w-full h-1 bg-[#E8E8E8] rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-emerald-600 transition-all"
              style={{
                width: `${
                  totalCount > 0
                    ? ((totalCount - missingCount) / totalCount) * 100
                    : 0
                }%`,
              }}
            />
          </div>

          {/* Badges champs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(completeness).map(([key, info]) => {
              const isLegalRequired =
                rules.legallyRequired?.includes(key);

              return (
                <div
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                    info.status
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}
                  title={
                    info.status
                      ? key === "images"
                        ? ui.nVisuals(Array.isArray(info.data) ? info.data.length : 0)
                        : key === "valuation"
                        ? formatPriceForPDF(info.data)
                        : String(info.data || "")
                      : isLegalRequired
                      ? ui.mandatory
                      : ui.recommended
                  }
                >
                  {info.status ? (
                    <CheckCircle
                      size={12}
                      className="flex-shrink-0"
                    />
                  ) : (
                    <AlertCircle
                      size={12}
                      className="flex-shrink-0"
                    />
                  )}
                  <span className="font-medium">
                    {fl(key)}
                    {isLegalRequired ? " *" : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Contact pour la vente : Expert / Avocat */}
          <div className="border-t border-[#E8E8E8] pt-4 mb-2">
            <p className="text-xs font-medium text-[#666] mb-2">
              {ui.responsible}
            </p>

            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setContactMode("expert")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  contactMode === "expert"
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-white text-[#666] border border-[#E8E8E8] hover:border-[#1A1A1A]"
                }`}
              >
                {ui.expert}
              </button>
              <button
                onClick={() => setContactMode("lawyer")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  contactMode === "lawyer"
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-white text-[#666] border border-[#E8E8E8] hover:border-[#1A1A1A]"
                }`}
              >
                {ui.lawyer}
              </button>
            </div>

            {contactMode === "expert" && (
              <div className="p-3 bg-[#FAFAFA] rounded-lg border border-[#E8E8E8] space-y-2">
                <label className="text-xs text-[#666]">
                  {ui.pickExpert}
                </label>
                <select
                  value={selectedExpertId}
                  onChange={(e) =>
                    setSelectedExpertId(e.target.value || "")
                  }
                  className="w-full border border-[#E8E8E8] rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#1A1A1A]"
                >
                  <option value="">
                    {ui.noExpert}
                  </option>
                  {experts.map((exp) => {
                    const fullName = `${exp.first_name || ""} ${
                      exp.last_name || ""
                    }`.trim();
                    return (
                      <option key={exp.id} value={exp.id}>
                        {fullName || ui.noNameExpert}{" "}
                        {exp.company ? `(${exp.company})` : ""}
                      </option>
                    );
                  })}
                </select>

                {selectedExpert && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#666]">
                      {ui.emailLbl}{" "}
                      <span className="font-medium">
                        {selectedExpert.email || ui.notProvided}
                      </span>
                    </p>
                    {selectedExpert.email && (
                      <button
                        onClick={contactExpert}
                        className="inline-flex items-center gap-1 text-xs text-[#1A1A1A] underline hover:no-underline"
                      >
                        <Mail size={12} />
                        {ui.contact}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {contactMode === "lawyer" && (
              <div className="grid grid-cols-2 gap-2 p-3 bg-[#FAFAFA] rounded-lg border border-[#E8E8E8]">
                <input
                  type="text"
                  placeholder={ui.phName}
                  value={lawyerInfo.name}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      name: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-2"
                />
                <input
                  type="text"
                  placeholder={ui.phFirm}
                  value={lawyerInfo.firm}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      firm: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-2"
                />
                <input
                  type="email"
                  placeholder={ui.phEmail}
                  value={lawyerInfo.email}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      email: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-1"
                />
                <input
                  type="text"
                  placeholder={ui.phPhone}
                  value={lawyerInfo.phone}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      phone: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-1"
                />
                <p className="col-span-2 text-[11px] text-[#999] mt-1">
                  {ui.lawyerNeed}
                </p>
              </div>
            )}
          </div>

          {/* Attestations FO / propriétaire */}
          <div className="border-t border-[#E8E8E8] pt-4 mb-2 space-y-2">
            {needsAuthAttestation && (
              <label className="flex items-start gap-2 text-[11px] text-[#444] bg-[#FDFBF4] border border-amber-200 rounded-md px-3 py-2">
                <input
                  type="checkbox"
                  checked={userCheckedAuthenticity}
                  onChange={(e) =>
                    setUserCheckedAuthenticity(e.target.checked)
                  }
                  className="mt-[2px]"
                />
                <span>
                  {ui.ownerFO}{" "}<span className="font-semibold">{ui.authBold}</span>{" "}{ui.authRest}
                </span>
              </label>
            )}

            {needsPropertyAttestation && (
              <label className="flex items-start gap-2 text-[11px] text-[#444] bg-[#F5FAFF] border border-sky-200 rounded-md px-3 py-2">
                <input
                  type="checkbox"
                  checked={userCheckedProperty}
                  onChange={(e) =>
                    setUserCheckedProperty(e.target.checked)
                  }
                  className="mt-[2px]"
                />
                <span>
                  {ui.ownerFO}{" "}<span className="font-semibold">{ui.propBold}</span>{" "}{ui.propRest}
                </span>
              </label>
            )}
          </div>

          {/* Message de blocage si non autorisé */}
          {!isSaleAuthorized && (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠️ {ui.blockHead(uiCountry)}
              <br />• {ui.blockFields} (
              {legalRequiredKeys
                .map((key) => fl(key))
                .filter(Boolean)
                .join(", ") || "OK"}
              )
              <br />• {ui.blockContact}
              {needsAuthAttestation
                ? ` • ${ui.blockAuth}`
                : ""}
              {needsPropertyAttestation
                ? ` • ${ui.blockProp}`
                : ""}
              .
            </p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[#E8E8E8] px-6 py-3 flex items-center justify-between flex-shrink-0 bg-[#FAFAFA]">
          <button
            onClick={() => setShowNDA(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] hover:border-[#1A1A1A] transition-colors"
          >
            <FileText size={14} />
            {ui.viewNda}
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] text-[#333] hover:border-[#1A1A1A] transition-colors"
            >
              {ui.close}
            </button>
            <button
              onClick={generateSaleDossier}
              disabled={!isSaleAuthorized}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                isSaleAuthorized
                  ? "bg-[#1A1A1A] text-white hover:bg-[#333]"
                  : "bg-[#E8E8E8] text-[#999] cursor-not-allowed"
              }`}
            >
              <Download size={14} />
              {ui.download}
            </button>
          </div>
        </div>

        {/* Modal NDA plein écran dans la carte */}
        {showNDA && (
          <div className="absolute inset-0 bg-white rounded-xl p-6 overflow-auto z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif text-[#1A1A1A]">
                {ui.ndaTitle}
              </h3>
              <button
                onClick={() => setShowNDA(false)}
                className="text-[#666] hover:text-[#1A1A1A]"
              >
                <X size={20} />
              </button>
            </div>
            <textarea
              value={ndaText}
              onChange={(e) => setNdaText(e.target.value)}
              rows={20}
              className="w-full min-h-[500px] border border-[#E8E8E8] rounded-lg p-4 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] leading-relaxed"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowNDA(false)}
                className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] hover:border-[#1A1A1A]"
              >
                {ui.close}
              </button>
              <button
                onClick={() => {
                  setShowNDA(false);
                  alert(
                    ui.ndaUpdated
                  );
                }}
                className="px-3 py-1.5 text-xs rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333]"
              >
                {ui.ndaSave}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
